/* The /step-viewer page.
 *
 * Reading happens in a worker (step-worker.js → occt-import-js, OpenCascade in
 * WebAssembly), the scene is built there too (step-mesh.js), and the page only
 * draws (step-view.js, three.js r147) and talks to the person. A new worker per
 * file, terminated as soon as the parts are here: WebAssembly memory only
 * grows, and a 300-MB assembly's peak would otherwise stay with the tab.
 *
 * Every number on the page is in millimetres internally — the reader is asked
 * for millimetres — and SHOWN in the unit the file itself was written in
 * (mm, inch, …), with a switch. The STL is always written in millimetres:
 * STL has no unit field, and every slicer reads its numbers as millimetres.
 */
import { reportShape } from '../file-shape.js'
import { suggestOtherTool } from '../file-signature.js'

const T = window.__SV_I18N || {}
const URLS = window.__SV_URLS || {}
const SLUG = 'step-viewer'
const t = (k, fallback, vars) => {
  let s = T[k] || fallback || k
  if (vars) {
    for (const [n, v] of Object.entries(vars).sort((a, b) => b[0].length - a[0].length)) s = s.split(':' + n).join(String(v))
  }
  return s
}
const $ = s => document.querySelector(s)
const el = (tag, cls, text) => {
  const n = document.createElement(tag)
  if (cls) n.className = cls
  if (text != null) n.textContent = text
  return n
}
const locale = () => (window.__intlLocale ? window.__intlLocale() : undefined)
const stage = (name, data) => { try { window.__stage && window.__stage(name, data) } catch (_) {} }
const feature = (k, data) => { try { window.__aeFeature && window.__aeFeature(k, data) } catch (_) {} }

const state = {
  file: null, busy: false, worker: null, limit: null, view: null,
  scene: null, facts: null, notes: null, stats: null, timing: null,
  unit: 'mm', fileUnit: null, sel: -1, group: null, tick: null, started: 0,
}

/* ------------------------------------------------------------- numbers --- */

const MM_PER = { mm: 1, cm: 10, m: 1000, in: 25.4, ft: 304.8, um: 0.001 }
const unitLabel = u => t('u_' + u, u)
/* the unit used for SHOWING: the file's own, except the exotic ones */
const showUnit = u => (u === 'in' || u === 'mm' || u === 'cm' || u === 'm' || u === 'ft') ? u : 'mm'

function fmtLen(mm, unit) {
  const v = mm / MM_PER[unit]
  const abs = Math.abs(v)
  const digits = unit === 'in' || unit === 'ft' ? (abs < 10 ? 3 : 2) : unit === 'm' ? 4 : (abs < 100 ? 2 : 1)
  return v.toLocaleString(locale(), { maximumFractionDigits: digits, minimumFractionDigits: 0 })
}

function fmtVolume(mm3, unit) {
  if (unit === 'in' || unit === 'ft') {
    const v = mm3 / Math.pow(25.4, 3)
    return v.toLocaleString(locale(), { maximumFractionDigits: v < 10 ? 3 : 1 }) + ' ' + t('u_in3', 'in³')
  }
  const cm3 = mm3 / 1000
  return cm3.toLocaleString(locale(), { maximumFractionDigits: cm3 < 10 ? 3 : cm3 < 1000 ? 1 : 0 }) + ' ' + t('u_cm3', 'cm³')
}

const fmtBytes = n => {
  const u = [t('unit_b', 'B'), t('unit_kb', 'KB'), t('unit_mb', 'MB')]
  let i = 0, v = n
  while (v >= 1024 && i < u.length - 1) { v /= 1024; i++ }
  return v.toLocaleString(locale(), { maximumFractionDigits: v < 10 && i ? 1 : 0 }) + ' ' + u[i]
}

function sizeText(b, unit) {
  const dims = [b[3] - b[0], b[4] - b[1], b[5] - b[2]].map(v => fmtLen(v, unit))
  return dims.join(' × ') + ' ' + unitLabel(unit)
}

/* ---------------------------------------------------------- status box --- */

function setStatus(msg, kind = 'info', opts = {}) {
  const box = $('#sv-status')
  if (!box) return
  box.hidden = !msg
  box.className = 'sv-status tc-msg' + (kind === 'error' ? ' bad' : kind === 'warn' ? ' warn' : '')
  box.textContent = ''
  if (!msg) return
  const p = el('p', 'sv-status-text', msg)
  box.append(p)
  if (opts.progress != null) {
    const bar = el('div', 'sv-bar')
    const fill = el('span', 'sv-bar-fill')
    if (opts.progress < 0) bar.classList.add('sv-bar--busy')
    else fill.style.width = Math.max(2, Math.min(100, opts.progress * 100)) + '%'
    bar.append(fill)
    box.append(bar)
  }
  if (opts.cancel) {
    const b = el('button', 'tc-btn ghost sm sv-cancel', t('cancel', 'Cancel'))
    b.type = 'button'
    b.addEventListener('click', cancel)
    box.append(b)
  }
  if (opts.actions) {
    const row = el('div', 'tc-actions sv-status-actions')
    for (const a of opts.actions) {
      const b = el('button', 'tc-btn sm' + (a.ghost ? ' ghost' : ''), a.label)
      b.type = 'button'
      b.addEventListener('click', a.on)
      row.append(b)
    }
    box.append(row)
  }
}

/* A model this big may not fit: OpenCascade needs roughly 25–45 times the
   STEP's size in WebAssembly memory, and a tab gets 2 GB of it at most
   (measured 2026-11-05: a 1.9-MB NIST part took 86 MB; an 86-MB Fusion
   assembly hit the 2-GB ceiling after 5½ minutes of reading and came out
   with no surfaces at all). Asked BEFORE the daily unit is spent, so walking
   away costs nothing. */
/* Beyond this a STEP does not open in a tab at all: an 86-MB assembly already
   ran into the 2-GB ceiling, a 241-MB one read for 46 minutes to the same end.
   The picked file is refused by the zone (max-mb="150" in the blade); this is
   the same number for what a ZIP or a .stpZ turns out to hold. */
const HARD_MB = 150

function bigThresholdMb() {
  let small = false
  try {
    const dm = navigator.deviceMemory || 0
    small = (dm && dm <= 4) || (window.matchMedia && window.matchMedia('(pointer:coarse)').matches && Math.min(screen.width, screen.height) < 900)
  } catch (_) {}
  return small ? 15 : 50
}

function confirmBig(sizeBytes) {
  const mb = sizeBytes / 1048576
  if (mb < bigThresholdMb()) return Promise.resolve(true)
  return new Promise(resolve => {
    stage('big_file_asked', { mb: Math.round(mb) })
    setStatus(t('big_warn', 'This model is :mb MB.', { mb: Math.round(mb).toLocaleString(locale()) }), 'warn', {
      actions: [
        { label: t('big_go', 'Open anyway'), on: () => { stage('big_file_go', { mb: Math.round(mb) }); resolve(true) } },
        { label: t('cancel', 'Cancel'), ghost: true, on: () => { setStatus(''); resolve(false) } },
      ],
    })
  })
}

/* ------------------------------------------------------------- loading --- */

let threeLoad = null
function loadThree() {
  if (window.THREE && window.THREE.OrbitControls) return Promise.resolve()
  if (threeLoad) return threeLoad
  const vs = window.__vendorScript
  if (typeof vs !== 'function') return Promise.reject(new Error('no __vendorScript'))
  threeLoad = vs(URLS.three, { expect: 'THREE', label: 'three' })
    .then(() => vs(URLS.orbit, { expect: () => !!(window.THREE && window.THREE.OrbitControls), label: 'three_orbit' }))
    .then(() => {}, err => { threeLoad = null; throw err })
  return threeLoad
}

function endWorker() {
  if (state.worker) { try { state.worker.terminate() } catch (_) {} }
  state.worker = null
  if (state.tick) { clearInterval(state.tick); state.tick = null }
}

function cancel() {
  if (!state.busy) return
  const pending = state.settle
  state.settle = null
  endWorker()
  state.busy = false
  state.limit && state.limit.abort && state.limit.abort()
  state.limit = null
  setStatus(t('cancelled', 'Stopped. Pick the file again to start over.'), 'warn')
  stage('cancelled', { s: Math.round((Date.now() - state.started) / 1000) })
  if (pending) pending({ ok: false, code: 'cancelled' })
}

/* The reader gives no progress while it parses (one synchronous call into
   OpenCascade), so the page says what it is doing and for how long — a
   counter is what tells a person the tab is alive. */
function phaseText(m) {
  const s = Math.round((Date.now() - state.started) / 1000)
  if (m.phase === 'engine') {
    const mb = n => (n / 1048576).toLocaleString(locale(), { maximumFractionDigits: 1 })
    return m.total ? t('ph_engine', 'Loading the CAD reader: :got of :all MB', { got: mb(m.loaded || 0), all: mb(m.total) }) : t('ph_engine_plain', 'Loading the CAD reader…')
  }
  if (m.phase === 'compile') return t('ph_compile', 'Starting the CAD reader…')
  if (m.phase === 'read') {
    const big = state.file && state.file.size > 20 * 1048576
    return t(big ? 'ph_read_big' : 'ph_read', 'Reading the model… :s s', { s })
  }
  if (m.phase === 'build') return m.of ? t('ph_build', 'Building the view: part :n of :all', { n: m.n || 0, all: m.of }) : t('ph_build_plain', 'Building the view…')
  return t('ph_read', 'Reading the model… :s s', { s })
}

function read(file) {
  return new Promise(done => {
    const resolve = m => { state.settle = null; done(m) }
    state.settle = resolve
    endWorker()
    let w
    try { w = new Worker(URLS.worker) } catch (err) { resolve({ ok: false, code: 'worker-start', detail: String(err && err.message || err) }); return }
    state.worker = w
    let last = { phase: 'engine' }
    const show = () => {
      const prog = last.phase === 'engine' && last.total ? (last.loaded || 0) / last.total
        : last.phase === 'build' && last.of ? (last.n || 0) / last.of : -1
      setStatus(phaseText(last), 'info', { progress: prog, cancel: true })
    }
    state.tick = setInterval(show, 1000)
    show()
    w.onmessage = ev => {
      const m = ev.data || {}
      if (m.type === 'confirm') {
        /* an archive held more than its own size promised */
        clearInterval(state.tick)
        state.tick = null
        confirmBig(m.mb * 1048576).then(go => {
          if (state.worker !== w) return
          if (!go) { cancel(); return }
          state.tick = setInterval(show, 1000)
          show()
          w.postMessage({ type: 'go' })
        })
        return
      }
      if (m.type === 'progress') {
        if (m.phase !== last.phase) stage('phase_' + m.phase)
        last = m
        show()
        return
      }
      if (m.type === 'done') { endWorker(); resolve(m) }
    }
    w.onerror = ev => {
      /* a reader that blew up (out of memory, mostly) takes only its worker */
      endWorker()
      resolve({ ok: false, code: 'worker-crashed', detail: String(ev && ev.message || '') })
    }
    file.arrayBuffer().then(bytes => {
      if (state.worker !== w) return
      w.postMessage({ type: 'open', bytes, name: file.name, urls: URLS.engine, askMb: bigThresholdMb(), hardMb: HARD_MB }, [bytes])
    }, err => {
      endWorker()
      resolve({ ok: false, code: 'file-read', detail: String(err && err.message || err) })
    })
  })
}

/* ------------------------------------------------------------ refusals --- */

const CYRILLIC_UI = /^(ru|uk)$/.test(window.__LOCALE || '')
const APP_NAMES = {
  kompas: CYRILLIC_UI ? 'КОМПАС-3D' : 'KOMPAS-3D', solidworks: 'SolidWorks', inventor: 'Autodesk Inventor', fusion: 'Autodesk Fusion',
  catia: 'CATIA', nx_creo: 'Siemens NX / PTC Creo', solidedge: 'Solid Edge', parasolid: 'Parasolid',
  rhino: 'Rhino', freecad: 'FreeCAD',
}

function link(slug, text) {
  const a = el('a', null, text)
  a.href = `/${window.__LOCALE || 'en'}/${slug}`
  return a
}

function refuse(res, file) {
  const code = res.code
  const sn = res.sniff || {}
  const box = $('#sv-status')
  let msg
  let after = null
  if (code === 'not-cad' && sn.kind === 'native' && APP_NAMES[sn.app]) {
    msg = t('err_native', 'This is a :app file (.:ext).', { app: APP_NAMES[sn.app], ext: sn.ext })
  } else if (code === 'not-cad' && sn.kind === 'native' && sn.app === 'mesh') {
    msg = t('err_mesh', 'This is a triangle mesh (.:ext), not a CAD model.', { ext: sn.ext || 'stl' })
    after = ['3d-stl-viewer', t('link_stl', 'Open it in the STL viewer')]
  } else if (code === 'not-cad' && sn.kind === 'native' && sn.app === 'drawing') {
    msg = t('err_drawing', 'This is a 2D drawing (.:ext), not a 3D model.', { ext: sn.ext })
    after = ['dwg-viewer', t('link_dwg', 'Open it in the DWG viewer')]
  } else if (code === 'not-cad' && sn.kind === 'native') {
    msg = t('err_native_other', 'This format (.:ext) is not read here.', { ext: sn.ext })
  } else if (code === 'not-cad' && sn.kind === 'stepxml') {
    msg = t('err_stepxml', 'This is STEP in XML form (ISO 10303-28).')
  } else if (code === 'not-cad' && sn.kind === 'empty') {
    msg = t('err_empty', 'The file is empty.')
  } else if (code === 'not-cad') {
    msg = t('err_not_cad', 'This is not a STEP, IGES or BREP file.')
  } else if (code === 'zip-no-cad') {
    const z = res.zip || {}
    msg = t('err_zip_no_cad', 'The archive has no STEP, IGES or BREP file inside.') + (z.names && z.names.length ? ' ' + t('zip_inside', 'Inside: :names', { names: z.names.slice(0, 5).join(', ') + (z.total > 5 ? '…' : '') }) : '')
  } else if (code === 'zip-encrypted') {
    msg = t('err_zip_encrypted', 'The archive is password-protected.')
  } else if (/^zip-|^unpack-/.test(code)) {
    msg = t('err_zip_other', 'This archive could not be unpacked here.')
  } else if (code === 'too-big') {
    msg = t('err_too_big', 'This model is :mb MB — more than a browser tab can take apart.', { mb: (res.mb || 0).toLocaleString(locale()) })
  } else if (code === 'memory' || code === 'worker-crashed' || code === 'mesh-failed') {
    msg = t('err_memory', 'The model did not fit in the memory this browser tab has.')
  } else if (code === 'engine-load' || code === 'worker-start') {
    msg = t('err_engine', 'The CAD reader did not load.')
  } else if (code === 'no-geometry' && res.facts && res.facts.extRefs && res.facts.extRefs.length) {
    const names = res.facts.extRefs
    msg = t('err_external', 'This assembly keeps its parts in other files: :names.', { names: names.slice(0, 6).join(', ') + (names.length > 6 ? '…' : '') })
  } else if (code === 'no-geometry') {
    msg = t('err_no_geometry', 'The file was read, but it holds no solids or surfaces to draw.')
  } else if (code === 'read-failed' || code === 'read-exception') {
    msg = t('err_read', 'The reader could not make out the geometry in this file.')
  } else if (code === 'webgl') {
    msg = t('err_webgl', 'This browser cannot draw 3D (WebGL is off).')
  } else {
    msg = t('err_read', 'The reader could not make out the geometry in this file.')
  }
  setStatus(msg, 'error')
  if (after && box) {
    const p = el('p', 'sv-status-more')
    p.append(link(after[0], after[1]))
    box.append(p)
  }
  stage('error', { code })
  if (code === 'not-cad' && sn.kind === 'unknown') suggestOtherTool(file, SLUG, box, { text: t('suggest_text'), link: t('suggest_link') })
  state.limit && state.limit.abort && state.limit.abort()
  state.limit = null
}

function report(res, file) {
  try {
    const f = res.facts || {}
    reportShape({
      format: f.kind || (res.sniff && res.sniff.kind) || 'step',
      version: f.schema || null,
      size: file.size,
      ok: !!res.ok,
      reason: res.ok ? null : res.code,
      flags: [].concat(
        res.ok ? [] : [res.code, res.sniff && res.sniff.kind ? 'sniff-' + res.sniff.kind : null, res.sniff && res.sniff.app ? 'app-' + res.sniff.app : null].filter(Boolean),
        f.unit ? ['unit-' + f.unit] : [],
        res.notes && res.notes.zip ? ['zip'] : [],
        res.notes && res.notes.gzip ? ['gzip'] : [],
      ),
      counts: res.ok ? {
        parts: res.stats.parts, tris: res.stats.tris, faces: res.stats.faces, open: res.stats.open,
        untess: res.stats.untessellated, readMs: res.timing.readMs, buildMs: res.timing.buildMs, engineMs: res.timing.engineMs,
      } : {},
    })
  } catch (_) { /* telemetry never breaks the tool */ }
}

/* -------------------------------------------------------------- opening --- */

async function onFile(file) {
  if (!file || state.busy) return
  state.busy = true
  state.file = file
  state.limit = null
  state.started = Date.now()
  $('#sv-workspace').hidden = true
  $('#sv-summary').hidden = true
  stage('file_chosen', { mb: Math.round((file.size || 0) / 1048576) })
  if (!(await confirmBig(file.size))) { state.busy = false; return }
  /* The unit is the model: opening the same file again today is free, and
     every STL taken from it belongs to the same unit. */
  state.limit = window.__toolLimit ? window.__toolLimit.begin(SLUG, file.name + ':' + file.size) : null
  if (window.__toolLimit && !state.limit) { state.busy = false; return }
  /* three.js arrives while the file is read — not before a file is picked */
  const three = loadThree().then(() => true, () => false)
  let res
  try {
    res = await read(file)
  } catch (err) {
    res = { ok: false, code: 'read-exception', detail: String(err && err.message || err) }
  }
  if (!state.busy) return /* cancelled */
  report(res, file)
  if (!res.ok) {
    state.busy = false
    refuse(res, file)
    if (res.code === 'memory' || res.code === 'worker-crashed' || res.code === 'mesh-failed') window.__aeDiagnostic && window.__aeDiagnostic({ issue: 'step_memory', mb: Math.round(file.size / 1048576) })
    return
  }
  const ok = await three
  if (!ok) { state.busy = false; refuse({ code: 'engine-load' }, file); return }
  try {
    show(res)
  } catch (err) {
    state.limit && state.limit.abort && state.limit.abort()
    state.limit = null
    if (err && err.code === 'WEBGL_UNAVAILABLE') refuse({ code: 'webgl' }, file)
    else {
      setStatus(t('err_read', 'The reader could not make out the geometry in this file.'), 'error')
      window.__handleError && window.__handleError(err, 'step_viewer_show', { say: t('err_read') })
    }
  } finally {
    state.busy = false
  }
}

function show(res) {
  state.scene = res.scene
  state.facts = res.facts || {}
  state.notes = res.notes || {}
  state.stats = res.stats
  state.timing = res.timing || {}
  state.fileUnit = state.facts.unit || null
  state.unit = showUnit(state.fileUnit || 'mm')
  state.sel = -1
  state.group = null
  setStatus('')
  $('#sv-workspace').hidden = false
  if (!state.view) {
    state.view = new window.StepView($('#sv-view'), { dark: readTheme(), onPick: i => { state.group = null; select(i, false) } })
  }
  state.view.setParts(res.scene.parts)
  syncMode()
  renderTree()
  renderModel()
  renderSelection()
  renderSummary()
  /* the "solids only" switch matters only when the model mixes both */
  const closedN = res.scene.parts.filter(p => !p.open && p.tris).length
  $('#sv-stl-solids').hidden = !(closedN && closedN < res.scene.parts.length)
  syncThemeButton()
  stage('complete')
  state.limit && state.limit.commit && state.limit.commit()
  window.__aeToolComplete && window.__aeToolComplete({
    kind: state.facts.kind, schema: state.facts.schema || null, unit: state.facts.unit || null,
    parts: res.stats.parts, tris: res.stats.tris, readMs: state.timing.readMs, mb: Math.round(state.facts.bytes / 1048576),
    zip: !!state.notes.zip,
  })
}

/* --------------------------------------------------------------- panels --- */

function unionBox(indices) {
  const b = [Infinity, Infinity, Infinity, -Infinity, -Infinity, -Infinity]
  for (const i of indices) {
    const p = state.scene.parts[i].bbox
    for (let k = 0; k < 3; k++) { if (p[k] < b[k]) b[k] = p[k]; if (p[k + 3] > b[k + 3]) b[k + 3] = p[k + 3] }
  }
  return b
}

function allIndices() { return state.scene.parts.map((_, i) => i) }

/* Volume is a property of closed solids only; open surfaces (helper planes,
   IGES surface models) are left out of the sum, and a selection made of them
   alone has none. */
function volumeOf(indices) {
  let v = 0
  let closed = 0
  for (const i of indices) {
    const p = state.scene.parts[i]
    if (p.open || !p.tris) continue
    v += p.volume
    closed++
  }
  return closed ? v : null
}

function stat(label, value, sub) {
  const s = el('div', 'tc-stat')
  s.append(el('div', 'tc-stat-l', label))
  const v = el('div', 'tc-stat-v sv-stat-v', value)
  s.append(v)
  if (sub) s.append(el('div', 'tc-stat-s', sub))
  return s
}

function renderModel() {
  const host = $('#sv-model')
  host.textContent = ''
  const all = allIndices()
  const b = unionBox(all)
  const dims = el('div', 'tc-stats sv-dims')
  const axes = ['X', 'Y', 'Z']
  for (let k = 0; k < 3; k++) dims.append(stat(axes[k], fmtLen(b[k + 3] - b[k], state.unit), unitLabel(state.unit)))
  host.append(dims)
  const vol = volumeOf(all)
  const line = el('p', 'tc-note sv-modelnote')
  const unique = new Set(state.scene.parts.map(p => p.name)).size
  line.textContent = t('parts_line', 'Parts: :n (different: :u)', { n: state.scene.parts.length.toLocaleString(locale()), u: unique.toLocaleString(locale()) })
    + (vol != null ? ' · ' + t('volume_line', 'Volume: :v', { v: fmtVolume(vol, state.unit) }) : '')
  host.append(line)
  for (const b2 of document.querySelectorAll('#sv-units .tc-seg-b')) {
    const on = b2.dataset.unit === state.unit
    b2.classList.toggle('on', on)
    b2.setAttribute('aria-pressed', String(on))
  }
}

function renderSelection() {
  const host = $('#sv-sel')
  host.textContent = ''
  const one = state.sel >= 0 ? [state.sel] : null
  const grp = state.group
  const indices = one || (grp ? grp.parts : null)
  $('#sv-sel-panel').hidden = !indices
  $('#sv-stl-part').hidden = !indices
  if (!indices) return
  const name = one ? (state.scene.parts[state.sel].name || t('unnamed', 'Unnamed part')) : (grp.name || t('unnamed_group', 'Unnamed assembly'))
  host.append(el('p', 'sv-sel-name', name))
  const b = unionBox(indices)
  host.append(el('p', 'sv-sel-size', sizeText(b, state.unit)))
  const vol = volumeOf(indices)
  if (vol != null) host.append(el('p', 'tc-note', t('volume_line', 'Volume: :v', { v: fmtVolume(vol, state.unit) })))
  else host.append(el('p', 'tc-note', t('open_part', 'An open surface, not a closed solid: it has no volume.')))
  if (!one) host.append(el('p', 'tc-note', t('group_line', 'Assembly of :n parts', { n: indices.length })))
  $('#sv-stl-part').textContent = one ? t('stl_part', 'STL of this part') : t('stl_group', 'STL of this assembly')
}

function select(i, fit) {
  state.sel = i
  if (state.view) {
    state.view.select(i)
    if (fit && i >= 0) state.view.fit([i])
  }
  for (const r of document.querySelectorAll('#sv-tree .sv-row.on')) r.classList.remove('on')
  if (i >= 0) {
    const row = document.querySelector(`#sv-tree .sv-row[data-part="${i}"]`)
    if (row) {
      row.classList.add('on')
      let d = row.parentElement
      while (d && d.id !== 'sv-tree') { if (d.tagName === 'DETAILS' && !d.open) d.open = true; d = d.parentElement }
      if (row.scrollIntoView) row.scrollIntoView({ block: 'nearest' })
    }
    feature('pick', {})
  }
  renderSelection()
}

/* ----------------------------------------------------------------- tree --- */

function partsOf(node) {
  if (node.part !== undefined) return [node.part]
  const out = []
  for (const c of node.children || []) { const sub = partsOf(c); for (let i = 0; i < sub.length; i++) out.push(sub[i]) }
  return out
}

function syncChecks() {
  const vis = new Set(state.view.visibleIndices())
  for (const cb of document.querySelectorAll('#sv-tree input[type=checkbox]')) {
    const ps = cb.__parts || []
    const on = ps.filter(p => vis.has(p)).length
    cb.checked = on > 0
    cb.indeterminate = on > 0 && on < ps.length
  }
}

function row(node) {
  const ps = partsOf(node)
  const r = el('div', 'sv-row')
  if (node.part !== undefined) r.dataset.part = String(node.part)
  const cb = document.createElement('input')
  cb.type = 'checkbox'
  cb.checked = true
  cb.__parts = ps
  cb.setAttribute('aria-label', t('show_hide', 'Show or hide'))
  cb.addEventListener('change', () => {
    state.view.setVisible(ps, cb.checked)
    syncChecks()
    feature('visibility', {})
  })
  const name = el('button', 'sv-name', node.name || (node.part !== undefined ? t('unnamed', 'Unnamed part') : t('unnamed_group', 'Unnamed assembly')))
  name.type = 'button'
  if (node.part === undefined) name.append(el('span', 'sv-count', ' ' + ps.length))
  name.addEventListener('click', ev => {
    ev.preventDefault()
    if (node.part !== undefined) { state.group = null; select(node.part === state.sel ? -1 : node.part, node.part !== state.sel) }
    else { select(-1, false); state.group = { name: node.name, parts: ps }; state.view.fit(ps); renderSelection() }
  })
  const iso = el('button', 'sv-iso', '◎')
  iso.type = 'button'
  iso.title = t('isolate', 'Show only this')
  iso.setAttribute('aria-label', t('isolate', 'Show only this'))
  iso.addEventListener('click', ev => {
    ev.preventDefault()
    state.view.isolate(ps)
    state.view.fit(ps)
    syncChecks()
    feature('isolate', { n: ps.length })
  })
  r.append(cb, name, iso)
  return r
}

/* Groups render their children on first open: an assembly of thousands of
   parts would otherwise build thousands of rows nobody looks at. */
function branch(node, depth) {
  if (node.part !== undefined) return row(node)
  const d = document.createElement('details')
  d.className = 'sv-group'
  const s = document.createElement('summary')
  s.append(row(node))
  d.append(s)
  const fill = () => {
    if (d.__filled) return
    d.__filled = true
    const box = el('div', 'sv-kids')
    for (const c of node.children || []) box.append(branch(c, depth + 1))
    d.append(box)
    syncChecks()
  }
  d.addEventListener('toggle', () => { if (d.open) fill() })
  if (depth < 1) { d.open = true; fill() }
  return d
}

function renderTree() {
  const host = $('#sv-tree')
  host.textContent = ''
  const root = state.scene.tree
  const top = (!root.name && root.children) ? root.children : [root]
  for (const n of top) host.append(branch(n, 0))
  $('#sv-parts-count').textContent = state.scene.parts.length.toLocaleString(locale())
  syncChecks()
}

/* -------------------------------------------------------------- summary --- */

function renderSummary() {
  const box = $('#sv-summary')
  box.textContent = ''
  box.hidden = false
  const f = state.facts
  const chips = el('div', 'tc-chips sv-chips')
  const chip = (label, value) => {
    if (value == null || value === '') return
    const c = el('span', 'sv-chip')
    c.append(el('b', null, label), document.createTextNode(' '), el('span', null, String(value)))
    chips.append(c)
  }
  const fmt = f.kind === 'step' ? 'STEP' + (f.schema ? ' ' + f.schema : '') : f.kind === 'iges' ? 'IGES' : 'BREP'
  chip(t('chip_format', 'Format'), fmt)
  chip(t('chip_units', 'Units in the file'), f.unit ? unitLabel(f.unit) : t('units_none', 'not stated'))
  chip(t('chip_system', 'Written by'), f.system)
  chip(t('chip_size', 'Size'), fmtBytes(f.bytes || (state.file && state.file.size) || 0))
  chip(t('chip_tris', 'Triangles'), state.stats.tris.toLocaleString(locale()))
  box.append(chips)
  const notes = el('ul', 'sv-notes')
  const note = txt => notes.append(el('li', null, txt))
  const n = state.notes
  if (n.zip) {
    note(t('note_zip', 'Opened :name from the archive.', { name: n.zip.name }))
    if (n.zip.sameKind > 1) note(t('note_zip_more', 'The archive holds :n files of this kind; the largest one is shown.', { n: n.zip.sameKind }))
  }
  if (n.gzip) note(t('note_gzip', 'The file was compressed (gzip) and has been unpacked.'))
  if (!f.unit && f.kind === 'brep') note(t('note_brep_units', 'BREP files do not store units; the numbers are shown as millimetres.'))
  else if (!f.unit) note(t('note_no_units', 'The file does not say its units clearly; millimetres are assumed.'))
  if (state.stats.untessellated) note(t('note_untess', ':n faces could not be built and are missing from the view and the STL.', { n: state.stats.untessellated }))
  if (state.stats.open) note(t('note_open', ':n parts are open surfaces rather than closed solids.', { n: state.stats.open }))
  note(t('note_read', 'Read in :s s.', { s: ((state.timing.totalMs || 0) / 1000).toLocaleString(locale(), { maximumFractionDigits: 1 }) }))
  box.append(el('h4', 'sv-sub', t('notes_title', 'What the file says about itself')), notes)
}

/* -------------------------------------------------------------- toolbar --- */

function syncMode() {
  for (const b of document.querySelectorAll('#sv-mode .tc-seg-b')) {
    const on = b.dataset.mode === state.view.mode
    b.classList.toggle('on', on)
    b.setAttribute('aria-pressed', String(on))
  }
}

function readTheme() {
  try {
    const saved = localStorage.getItem('step-viewer:bg')
    if (saved === 'dark') return true
    if (saved === 'light') return false
  } catch (_) {}
  return document.documentElement.getAttribute('data-theme') !== 'light'
}
function syncThemeButton() {
  const b = $('#sv-bg')
  if (b && state.view) b.setAttribute('aria-pressed', String(!state.view.dark))
}

/* ----------------------------------------------------------------- STL --- */

const stem = () => ((state.notes && state.notes.zip && state.notes.zip.name ? state.notes.zip.name.split('/').pop() : (state.file && state.file.name)) || 'model').replace(/\.(step|stp|stpz|p21|igs|iges|brep|brp|zip|gz)$/i, '').replace(/\.(step|stp|igs|iges)$/i, '') || 'model'
const safeName = s => (window.__outputName && window.__outputName.sanitize) ? window.__outputName.sanitize(s) : s.replace(/[\\/:*?"<>|]+/g, '_')

async function exportStl(btn, indices, suffix) {
  if (!state.scene || !indices.length) return
  btn.disabled = true
  const label = btn.textContent
  btn.textContent = t('stl_busy', 'Preparing STL…')
  try {
    /* a breath for the label to paint before a big assembly is written */
    await new Promise(r => setTimeout(r, 30))
    const items = indices.map(i => state.scene.parts[i])
    const bytes = window.StepMesh.stlBytes(items, 'Timbrica STEP viewer, millimetres')
    const name = safeName(stem() + (suffix ? '-' + suffix : '') + '.stl')
    await window.__downloadFile(new Blob([bytes], { type: 'model/stl' }), name)
    feature('export_stl', { parts: indices.length, whole: !suffix })
  } catch (err) {
    window.__handleError && window.__handleError(err, 'step_viewer_stl', { say: t('err_export', 'Could not write the file.') })
  } finally {
    btn.disabled = false
    btn.textContent = label
  }
}

/* ---------------------------------------------------------------- wiring --- */

function wire() {
  const zone = document.getElementById('svDrop')
  if (window.__bindUploaderFile) window.__bindUploaderFile(zone, file => onFile(file))
  else if (zone) zone.addEventListener('tool-uploader:file', ev => { const d = ev && ev.detail; const f = d && (d.file || (d.files && d.files[0])); if (f) onFile(f) })
  zone && zone.addEventListener('tool-uploader:clear', () => {
    if (state.busy) cancel()
    state.scene = null
    $('#sv-workspace').hidden = true
    $('#sv-summary').hidden = true
    if (state.view) state.view.clear()
    setStatus('')
  })
  for (const b of document.querySelectorAll('#sv-views [data-view]')) {
    b.addEventListener('click', () => { if (state.view) { state.view.view(b.dataset.view); feature('view', { v: b.dataset.view }) } })
  }
  for (const b of document.querySelectorAll('#sv-mode .tc-seg-b')) {
    b.addEventListener('click', () => { if (state.view) { state.view.setMode(b.dataset.mode); syncMode(); feature('mode', { m: b.dataset.mode }) } })
  }
  for (const b of document.querySelectorAll('#sv-units .tc-seg-b')) {
    b.addEventListener('click', () => { if (!state.scene) return; state.unit = b.dataset.unit; renderModel(); renderSelection(); feature('units', { u: state.unit }) })
  }
  $('#sv-fit').addEventListener('click', () => state.view && state.view.fit())
  $('#sv-showall').addEventListener('click', () => { if (!state.view) return; state.view.showAll(); state.view.fit(); syncChecks() })
  $('#sv-bg').addEventListener('click', () => {
    if (!state.view) return
    state.view.setDark(!state.view.dark)
    try { localStorage.setItem('step-viewer:bg', state.view.dark ? 'dark' : 'light') } catch (_) {}
    syncThemeButton()
  })
  const whole = $('#sv-whole')
  if (whole && typeof window.__wholeScreen === 'function') {
    const door = window.__wholeScreen($('#sv-stagebox'), {
      key: 'step-viewer',
      onChange: on => { whole.setAttribute('aria-pressed', String(!!on)); if (state.view) setTimeout(() => state.view.resize(), 60) },
    })
    whole.addEventListener('click', () => door.toggle())
  } else if (whole) whole.hidden = true
  $('#sv-stl-all').addEventListener('click', e => {
    if (!state.view) return
    let list = state.view.visibleIndices()
    /* helper planes of a PMI model would land in the print as stray sheets */
    const solids = $('#sv-stl-solids')
    if (solids && !solids.hidden && solids.querySelector('input').checked) {
      const only = list.filter(i => !state.scene.parts[i].open)
      if (only.length) list = only
    }
    exportStl(e.currentTarget, list, '')
  })
  $('#sv-stl-part').addEventListener('click', e => {
    if (state.sel >= 0) exportStl(e.currentTarget, [state.sel], state.scene.parts[state.sel].name || 'part')
    else if (state.group) exportStl(e.currentTarget, state.group.parts, state.group.name || 'assembly')
  })
  $('#sv-sel-only').addEventListener('click', () => {
    const ps = state.sel >= 0 ? [state.sel] : state.group ? state.group.parts : null
    if (!ps || !state.view) return
    state.view.isolate(ps)
    state.view.fit(ps)
    syncChecks()
  })
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', wire)
else wire()
